/**
 * Writes a user-supplied HTML fragment (an "embed code") into a container and runs
 * its scripts, in document order.
 *
 * Why this exists instead of a bare postscribe() call
 * ---------------------------------------------------
 * postscribe keeps ONE module-level "active stream" plus a shared queue, and only
 * releases the active stream once every <script> it wrote has fired load or error.
 * Every postscribe caller in the app shares that queue - including
 * ThirdPartyScriptsInjectorComponent, which pushes each tenant's tracking snippet
 * through it. A snippet that creates its own deferred <script> never lets postscribe
 * pop it off the stream's script stack, so the active stream is pinned forever and
 * every later write is parked in the queue for the rest of the page's life, silently
 * and with no error. That is what made rich-text HTML embeds render nothing in the
 * course player: the embed's write was queued behind a tenant script that never
 * finished.
 *
 * This writer owns no module-level state, so one embed - or one tenant script - can
 * never stop another one from rendering.
 */

/** A blocked, 404 or hanging script must not stall the rest of the fragment. */
const SCRIPT_LOAD_TIMEOUT_MS = 8000;

/** Marks a <script> we have already handed to the browser, so it runs exactly once. */
const RAN_ATTR = 'data-och-embed-ran';

/** Safety net for a script that keeps writing more scripts. */
const MAX_SCRIPTS = 200;

/**
 * Replaces the contents of `container` with `html` and executes any scripts it
 * contains. Resolves once every script has run (or timed out). Never rejects - a
 * broken embed must not take down the view that hosts it.
 */
export function writeEmbeddedHtml(container: HTMLElement, html: string): Promise<void> {
  return write(container, html, true);
}

/**
 * Same as writeEmbeddedHtml() but appends instead of replacing, for containers whose
 * existing children must survive (e.g. injecting a tenant's tracking snippet into
 * <head>).
 */
export function appendEmbeddedHtml(container: HTMLElement, html: string): Promise<void> {
  return write(container, html, false);
}

async function write(container: HTMLElement, html: string, clear: boolean): Promise<void> {
  if (!container || !html) {
    return;
  }

  const doc = container.ownerDocument ?? document;

  if (clear) {
    container.textContent = '';
  }
  container.appendChild(parseInert(doc, html));

  // Legacy embed snippets still call document.write(). Left unshimmed that wipes the
  // whole page (the document is already closed), so point it at the container.
  const restoreWrite = shimDocumentWrite(doc, container);
  try {
    await runScripts(container, doc);
  } catch (err) {
    console.error('Could not run an embedded HTML fragment', err);
  } finally {
    restoreWrite();
  }
}

/**
 * Parses markup without running anything. Scripts parsed into a <template> stay
 * inert even after they are moved into the live document, so they can only ever run
 * through the explicit rehydration in executeScript() - never twice.
 */
function parseInert(doc: Document, html: string): DocumentFragment {
  const template = doc.createElement('template');
  template.innerHTML = html;
  return template.content;
}

/**
 * Runs the fragment's scripts one at a time, waiting for each external script so
 * snippets that depend on load order still work. Re-queries after every script
 * because a script may have written more markup - and more scripts - into the
 * container.
 */
async function runScripts(container: HTMLElement, doc: Document): Promise<void> {
  for (let i = 0; i < MAX_SCRIPTS; i++) {
    const pending = container.querySelector<HTMLScriptElement>(`script:not([${RAN_ATTR}])`);
    if (!pending) {
      return;
    }
    await executeScript(pending, doc);
  }
  console.warn(`Embedded HTML stopped after ${MAX_SCRIPTS} scripts`);
}

/**
 * Swaps an inert parsed <script> for a freshly created one, which is what makes the
 * browser run it.
 */
async function executeScript(original: HTMLScriptElement, doc: Document): Promise<void> {
  // Mark first: if anything below fails, the script is still never retried.
  original.setAttribute(RAN_ATTR, '');

  const parent = original.parentNode;
  if (!parent) {
    // An earlier script removed this node before it could run.
    return;
  }

  const fresh = doc.createElement('script');
  for (const attr of Array.from(original.attributes)) {
    fresh.setAttribute(attr.name, attr.value);
  }
  fresh.text = original.textContent ?? '';

  const loaded = fresh.hasAttribute('src') ? waitForScript(fresh) : null;
  parent.replaceChild(fresh, original);
  if (loaded) {
    await loaded;
  }
}

/** Resolves on load, on error, or on timeout - so the chain can never hang. */
function waitForScript(script: HTMLScriptElement): Promise<void> {
  return new Promise<void>(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(finish, SCRIPT_LOAD_TIMEOUT_MS);
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', finish, { once: true });
  });
}

/**
 * Redirects document.write()/writeln() into the container while the fragment's
 * scripts run. Written markup is parsed inert, so any scripts it brings along are
 * picked up by the runScripts() loop.
 */
function shimDocumentWrite(doc: Document, container: HTMLElement): () => void {
  const originalWrite = doc.write;
  const originalWriteln = doc.writeln;

  const append = (markup: string) => {
    if (!markup) return;
    container.appendChild(parseInert(doc, markup));
  };

  doc.write = function (...parts: string[]) {
    append(parts.join(''));
  };
  doc.writeln = function (...parts: string[]) {
    append(parts.join('') + '\n');
  };

  return () => {
    doc.write = originalWrite;
    doc.writeln = originalWriteln;
  };
}
